import { ArrowLeft, Save } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const BuildingAdd = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  // 건물 추가 뮤테이션
  const addBuildingMutation = useMutation(
    (data) => api.post('/buildings', data),
    {
      onSuccess: () => {
        toast.success('건물이 성공적으로 추가되었습니다.');
        queryClient.invalidateQueries('buildings');
        navigate('/buildings');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '건물 추가에 실패했습니다.');
        setIsSubmitting(false);
      }
    }
  );

  const onSubmit = (data) => {
    setIsSubmitting(true);
    console.log('건물 추가 데이터:', data);
    console.log('토큰:', localStorage.getItem('token'));
    addBuildingMutation.mutate(data);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* 헤더 */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/buildings')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">건물 추가</h1>
            <p className="mt-1 text-sm text-gray-500">
              새로운 건물을 등록하세요
            </p>
          </div>
        </div>
      </div>

      {/* 건물 추가 폼 */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">기본 정보</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 건물명 */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  건물명 *
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '건물명을 입력해주세요' })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 아파트 101동"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

                             {/* 건물 유형 */}
               <div>
                 <label htmlFor="building_type" className="block text-sm font-medium text-gray-700 mb-2">
                   건물 유형 *
                 </label>
                 <select
                   id="building_type"
                   {...register('building_type', { required: '건물 유형을 선택해주세요' })}
                   className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                     errors.building_type ? 'border-red-300' : 'border-gray-300'
                   }`}
                 >
                   <option value="">건물 유형 선택</option>
                   <option value="apartment">아파트</option>
                   <option value="villa">빌라</option>
                   <option value="officetel">오피스텔</option>
                   <option value="commercial">상가</option>
                   <option value="mixed">복합건물</option>
                 </select>
                 {errors.building_type && (
                   <p className="mt-1 text-sm text-red-600">{errors.building_type.message}</p>
                 )}
               </div>

              {/* 주소 */}
              <div className="md:col-span-2">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  주소 *
                </label>
                <input
                  type="text"
                  id="address"
                  {...register('address', { required: '주소를 입력해주세요' })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.address ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 서울시 강남구 테헤란로 123"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>

              {/* 총 세대 수 */}
              <div>
                <label htmlFor="total_units" className="block text-sm font-medium text-gray-700 mb-2">
                  총 세대 수 *
                </label>
                <input
                  type="number"
                  id="total_units"
                  {...register('total_units', { 
                    required: '총 세대 수를 입력해주세요',
                    min: { value: 1, message: '최소 1세대 이상이어야 합니다' }
                  })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.total_units ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="예: 50"
                />
                {errors.total_units && (
                  <p className="mt-1 text-sm text-red-600">{errors.total_units.message}</p>
                )}
              </div>

                             {/* 건축년도 */}
               <div>
                 <label htmlFor="year_built" className="block text-sm font-medium text-gray-700 mb-2">
                   건축년도
                 </label>
                 <input
                   type="number"
                   id="year_built"
                   {...register('year_built', {
                     min: { value: 1900, message: '올바른 건축년도를 입력해주세요' },
                     max: { value: new Date().getFullYear(), message: '올바른 건축년도를 입력해주세요' }
                   })}
                   className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                     errors.year_built ? 'border-red-300' : 'border-gray-300'
                   }`}
                   placeholder="예: 2020"
                 />
                 {errors.year_built && (
                   <p className="mt-1 text-sm text-red-600">{errors.year_built.message}</p>
                 )}
               </div>
            </div>
          </div>

          {/* 상세 정보 */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">상세 정보</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                             {/* 관리사무소 연락처 */}
               <div>
                 <label htmlFor="management_phone" className="block text-sm font-medium text-gray-700 mb-2">
                   관리사무소 연락처
                 </label>
                 <input
                   type="tel"
                   id="management_phone"
                   {...register('management_phone')}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                   placeholder="예: 02-1234-5678"
                 />
               </div>

                             {/* 상태 */}
               <div>
                 <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                   상태
                 </label>
                 <select
                   id="status"
                   {...register('status')}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                 >
                   <option value="">상태 선택</option>
                   <option value="active">활성</option>
                   <option value="inactive">비활성</option>
                   <option value="maintenance">유지보수 중</option>
                 </select>
               </div>

              
            </div>
          </div>

          {/* 버튼 */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/buildings')}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting ? '저장 중...' : '건물 추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuildingAdd;
